<template>
  <div style="border: 1px solid #ccc" id="wangEditor">
    <Editor
      class="we-editor"
      style="min-height: 100px; overflow-y: hidden; text-align: left;"
      v-model="valueHtml"
      :defaultConfig="editorConfig()"
      :mode="mode"
      @onCreated="handleCreated"
      @onChange="handleChange"
    />
    <div class="toolbar-row">
      <Toolbar
        class="toolbar"
        :editor="editorRef"
        :defaultConfig="toolbarConfig"
        :mode="mode"
      />
      <div v-if="toolbarTip" class="toolbar-tip">{{ toolbarTip }}</div>
    </div>
  </div>
</template>
<script>
import '@wangeditor/editor/dist/css/style.css' // 引入 css
import { Editor, Toolbar } from '@wangeditor/editor-for-vue'
import { queUrl } from '@/config/base'

export default {
  name: 'WeEditor',
  components: { Editor, Toolbar },
  props: {
    preText: {
      type: String,
      default: ''
    },
    token: {
      type: String,
      default: ''
    },
    maxImageCount: {
      type: Number,
      default: 9
    },
    disabled: {
      type: Boolean,
      default: false
    },
    initValue: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    // 工具栏右侧提示文案（不传则不展示）
    toolbarTip: {
      type: String,
      default: ''
    },
    // 与 ImageUpload 一致的图片校验配置
    fileSize: {
      type: Number,
      default: 5 // MB
    },
    fileType: {
      type: Array,
      default: () => ['jpg', 'png']
    }
  },
  data() {
    return {
      mode: 'default',
      editorRef: null,
      valueHtml: '',
      uploadedImageCount: 0,
      toolbarConfig: {
        toolbarKeys: [
          'uploadImage' // 上传图片
        ]
      },
    }
  },
  watch: {
    initValue: {
      handler(val) {
        this.valueHtml = val
        // 当初始值变化时，重新统计图片数量
        this.$nextTick(() => {
          if (this.editorRef) {
            const children = this.editorRef.children || []
            this.updateImageCount(children)
          }
        })
      },
      immediate: true
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.valueHtml = this.initValue
    })
  },
  beforeDestroy() {
    const editor = this.editorRef
    if (editor == null) return
    editor.destroy()
  },
  methods: {
    // 统计当前编辑器中图片数量（递归统计 children 节点里的 image）
    // - children 可不传：默认从 editorRef.children 取
    // - maxImageCount 为 0 时不限制，但仍可用于外部展示计数
    getCurrentImageCount(children) {
      const nodes = children || (this.editorRef && this.editorRef.children) || []
      let count = 0
      const walk = (list) => {
        if (!Array.isArray(list)) return
        list.forEach(node => {
          if (node && node.type === 'image') {
            count++
          }
          if (node && Array.isArray(node.children) && node.children.length > 0) {
            walk(node.children)
          }
        })
      }
      walk(nodes)
      return count
    },
    editorConfig() {
      // 如果传入了 placeholder，使用传入的值；否则使用默认值
      const imageLimitText = this.maxImageCount === 0 ? '' : `，图片不超过${this.maxImageCount}张`
      const defaultPlaceholder = `请编辑文章正文，字数不超过5000${imageLimitText}。`
      return {
        placeholder: this.placeholder || defaultPlaceholder, // 可配置的提示文本
        maxLength: 5000,
        MENU_CONF: {
          uploadImage: {
            // 自定义上传
            customUpload: async (file, insertFn) => {
              // 检查图片数量限制（maxImageCount 为 0 时不限制）
              // 以编辑器"当前实际内容"为准，避免 uploadedImageCount 与实际不一致
              const currentCount = this.getCurrentImageCount()
              this.uploadedImageCount = currentCount
              if (this.maxImageCount > 0 && currentCount >= this.maxImageCount) {
                this.$message.warning(`最多只能上传${this.maxImageCount}张图片`)
                return
              }

              // 验证文件大小（与 ImageUpload 一致）
              if (this.fileSize) {
                const isLt = file.size / 1024 / 1024 < this.fileSize
                if (!isLt) {
                  this.$message.error(`文件大小不能超过 ${this.fileSize}MB!`)
                return
              }
              }

              // 验证文件类型（与 ImageUpload 一致：按扩展名判断）
              // if (this.fileType && this.fileType.length) {
              //   const ext = (file.name.split('.').pop() || '').toLowerCase()
              //   const ok = this.fileType.some(t => t.toLowerCase() === ext)
              //   if (!ok) {
              //     this.$message.error(`文件格式不正确，请上传${this.fileType.join('/')}格式文件!`)
              //   return
              //   }
              // }

              try {
                const formData = new FormData();
                formData.append('file', file);

                const headers = {
                  'Token': this.token,
                  'Content-Type': 'multipart/form-data'
                }

                const result = await this.$axios.post(`${queUrl}interface/ossClient/upload`, formData, { headers })

                if (result.data && result.data.code == 200) {
                  // 插入图片到编辑器
                  const imageUrl = result.data.data.viewUrl || result.data.data
                  insertFn(imageUrl, file.name, imageUrl)
                  // 更新图片计数
                  // 插入后再基于内容重新计算，确保一致
                  this.$nextTick(() => {
                    this.uploadedImageCount = this.getCurrentImageCount()
                    this.$emit('imageCountChange', this.uploadedImageCount)
                  })
                } else {
                  this.$message.error(result.data.msg || result.data.message || '上传失败')
                }
              } catch (error) {
                console.error('Upload error:', error)
                this.$message.error('上传失败，请重试')
              }
            }
          }
        }
      }
    },
    handleCreated(editor) {
      this.editorRef = editor // 创建富文本编辑器
      if (this.disabled) {
        this.editorRef.disable() // 禁用
      }
    },
    handleChange(info) {
      // 统计编辑器中的图片数量
      this.updateImageCount(info.children)
      this.$emit('getEditorContent', info.children, this.valueHtml)
      this.$emit('imageCountChange', this.uploadedImageCount)
      this.$emit('input', this.valueHtml)
    },
    updateImageCount(children) {
      // 保留原入口，但内部统一走 getCurrentImageCount
      this.uploadedImageCount = this.getCurrentImageCount(children)
    }
  }
}
</script>

<style scoped>
.toolbar-row {
  display: flex;
  align-items: flex-end;
  /* justify-content: space-between; */
  /* gap: calc(12 / 1920 * 100vw); */
  /* border-top: 1px solid #ccc; */
  padding-right: calc(12 / 1920 * 100vw);
}

.toolbar {
  /* flex: 1; */
  min-width: 0;
}

.toolbar-tip {
  flex: none;
  font-size: calc(15 / 1920 * 100vw);
  line-height: calc(30 / 1920 * 100vw);
  color: #00000066;
  white-space: nowrap;
  margin-left: calc(10 / 1920 * 100vw);
}

.w-e-text-placeholder {
  top: calc(10 / 1920 * 100vw);
  @media (max-width: 768px) {
    top: calc(10 / 750 * 100vw);
  }
}

::v-deep .w-e-text-container {
  min-height: calc(230 / 1920 * 100vw) !important;
  @media (max-width: 768px) {
    min-height: calc(200 / 750 * 100vw) !important;
  }
}

.w-e-selected-image-container {
  max-width: calc(800 / 1920 * 100vw);
}

::v-deep .w-e-bar-item {
  height: calc(80 / 1920 * 100vw);
  width: calc(80 / 1920 * 100vw);
  border: 1px solid #6C6C6C;
  padding: 0px;
  border-radius: 4px;
}
::v-deep .w-e-bar-item, .w-e-bar-item button {
  align-items: normal;
}
::v-deep .w-e-bar-item button {
  margin-top: calc(15 / 1920 * 100vw);
}
::v-deep .w-e-bar-item .disabled svg {
 color: #000000;
 fill: #000000;
}
::v-deep .w-e-bar svg {
 color: #000000;
 fill: #000000;
}

::v-deep .w-e-menu-tooltip-v5:after {
  content: "添加图片";
  font-size: calc(13 / 1920 * 100vw);
  color: #000000;
  margin-top: calc(45 / 1920 * 100vw);
  opacity: 1;
  border-bottom: none;
  visibility: visible;
  top: auto; 
}

.w-e-hover-bar .w-e-bar-item:nth-of-type(4),
.w-e-hover-bar .w-e-bar-item:nth-of-type(5) {
  display: none;
}
::v-deep .w-e-bar svg {
  height: 18px;
  width: 18px;
}


@media (max-width: 768px) {
  .toolbar-row {
    flex-wrap: wrap;
    align-items: flex-start;
    margin-top: calc(40 / 750 * 100vw);
  }
  .we-editor {
    border-bottom: 1px solid #e4e7ed;
  }
  .toolbar {
    width: 100%;
  }
  .toolbar-tip {
    width: 100%;
    white-space: normal;
    line-height: 1.4;
    margin-top: calc(20 / 750 * 100vw);
    font-size: calc(24 / 750 * 100vw);
  }
  ::v-deep .w-e-bar-item {
    height: calc(112 / 750 * 100vw);
    width: calc(112 / 750 * 100vw);
    border: 1px solid #6C6C6C;
    padding: 0px;
    border-radius: 4px;
  }
  ::v-deep .w-e-bar-item button {
    margin-top: calc(10 / 750 *100vw);
  }
  ::v-deep .w-e-menu-tooltip-v5:after {
  margin-top: calc(70 / 750 *100vw);
  font-size: calc(18 / 750 * 100vw);
}
}
</style>
